/**
 * Represents the status of a user in the self-guided system.
 * This class provides predefined statuses and ensures that only one instance
 * of each status is created and reused.
 */
export default class VerificationMethod {
  constructor(name) {
    this.name = name;
  }

  /**
   * Retrieves the instance of the verification method 'CreditCard'.
   * This method is used to represent the 'CreditCard' verification type.
   *
   * @returns {VerificationMethod} The instance representing the 'CreditCard' verification method.
   */
  static get CreditCard() {
    return this.getInstance('CreditCard');
  }

  /**
   * Retrieves the instance of the 'Identification' verification method.
   * This method is used to access the specific verification method type.
   *
   * @returns {VerificationMethod} The instance of the 'Identification' verification method.
   */
  static get Identification() {
    return this.getInstance('Identification');
  }

  /**
   * Gets the instance representing the 'Phone' verification method.
   * @returns {VerificationMethod} The instance of the 'Phone' verification method.
   */
  static get Phone() {
    return this.getInstance('Phone');
  }

  static fromKey(key) {
    const statusMap = {
      CreditCard: VerificationMethod.CreditCard,
      Identification: VerificationMethod.Identification,
      Phone: VerificationMethod.Phone,
    };
    return statusMap[key];
  }

  /**
   * This function returns an instance of the VerificationMethod class based on a given key, creating a new
   * instance if one does not already exist.
   *
   * @param key - a unique identifier used to create and retrieve instances of the VerificationMethod class.
   * It is used to ensure that only one instance of the VerificationMethod class is created for each unique key value.
   *
   * @returns returns an instance of the `VerificationMethod` class with the specified `key`.
   * If an instance with the specified `key` already exists, it returns that instance instead of creating a new one.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new VerificationMethod(key);
    }
    return this.instances[key];
  }

  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(VerificationMethod, 'instances', { value: {} });
